import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { adminChangeCredentials } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, KeyRound, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const AdminCredentialsForm = () => {
  const { adminToken } = useApp();
  const [form, setForm] = useState({
    current_password: "",
    new_username: "",
    new_password: "",
    confirm_password: "",
  });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast.error("Current password is required");
      return;
    }
    if (!form.new_username && !form.new_password) {
      toast.error("Enter new username or new password");
      return;
    }
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: form.current_password };
      if (form.new_username) payload.new_username = form.new_username;
      if (form.new_password) payload.new_password = form.new_password;
      await adminChangeCredentials(adminToken, payload);
      toast.success("Admin credentials updated. Please log in again next time.");
      setForm({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md p-5 md:p-6" data-testid="admin-credentials-section">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-[#2D5A27]" />
        <h2 className="font-heading font-semibold text-lg">Change Admin Login</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        अपना admin username और password बदलें ताकि सिर्फ आप ही dashboard पर login कर सकें। Change करने के बाद पुराने password से login नहीं होगा।
      </p>
      <form onSubmit={submit} className="space-y-4 max-w-lg">
        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 text-slate-500" /> Current Password
          </Label>
          <div className="relative mt-1.5">
            <Input
              type={show ? "text" : "password"}
              value={form.current_password}
              onChange={update("current_password")}
              data-testid="cred-current-password"
              className="h-11 bg-white pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              data-testid="cred-toggle-show"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1"
              aria-label="Toggle password visibility"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium">New Username (optional)</Label>
          <Input
            value={form.new_username}
            onChange={update("new_username")}
            placeholder="Leave empty to keep same"
            data-testid="cred-new-username"
            className="mt-1.5 h-11 bg-white"
          />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5 text-[#D97706]" /> New Password
          </Label>
          <Input
            type={show ? "text" : "password"}
            value={form.new_password}
            onChange={update("new_password")}
            placeholder="At least 6 characters (leave empty to keep same)"
            data-testid="cred-new-password"
            className="mt-1.5 h-11 bg-white"
          />
        </div>

        <div>
          <Label className="text-sm font-medium">Confirm New Password</Label>
          <Input
            type={show ? "text" : "password"}
            value={form.confirm_password}
            onChange={update("confirm_password")}
            data-testid="cred-confirm-password"
            className="mt-1.5 h-11 bg-white"
          />
        </div>

        <Button
          type="submit"
          disabled={saving}
          data-testid="save-credentials-btn"
          className="rounded-md bg-[#1E293B] hover:bg-[#0f172a] text-white font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-1.5" /> Update Credentials</>)}
        </Button>
      </form>
    </div>
  );
};

export default AdminCredentialsForm;
